// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import styled from '@emotion/styled'
import RemoveIcon from '@mui/icons-material/Remove'
import { Checkbox, ListItemText } from '@mui/material'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import cn from 'classnames'
import uniq from 'lodash/uniq'
import moment from 'moment'
import React from 'react'
import DeleteButton from '../../common/iconButtons/DeleteButton'
import DoneButton from '../../common/iconButtons/DoneButton'
import NoteButton from '../../common/iconButtons/NoteButton'
import type { UserType } from '../admin/users/userTypes'
import { getMomentFromTime } from '../cookingPlan/helpers'
import { i18n } from '../index'
import type { ShiftResponsibility } from '../shiftResponsibility/shiftResponsibilityTypes'
import css from './EmployeeRow.css'
import { positionOptions } from './StaffingPlan'
import type { StaffMember } from './types'
type EmployeeRowProps = {
  idx: number
  employee: StaffMember
  removeStaffMember: (...args: Array<any>) => any
  isEditing?: boolean
  valueChanged: (arg0: number, arg1: string, arg2: string) => void
  selectedEmployees: Array<StaffMember>
  toggleEditNote: (...args: Array<any>) => any
  editNote: boolean
  isToday: boolean
  shiftResponsibilities: Array<ShiftResponsibility>
  userType: UserType
  activityDescription: string
  openEmployeeRowDialog: (...args: Array<any>) => any
  submit: (...args: Array<any>) => any
  hasMultipleRows: boolean
  showLess: (...args: Array<any>) => any
  employeeIncentives: {
    sia: number
    sit: number
    tua: number
    tut: number
  }
}

const ResponsibilitySelect = styled(Select)`
  & .MuiSelect-select {
    height: auto;
    text-overflow: initial;
    overflow: visible;
    white-space: normal;
  }
`

const EmployeeRow = ({
  idx,
  employee,
  removeStaffMember,
  isEditing,
  valueChanged,
  selectedEmployees,
  toggleEditNote,
  editNote,
  isToday,
  shiftResponsibilities,
  userType,
  activityDescription,
  openEmployeeRowDialog,
  submit,
  hasMultipleRows,
  showLess,
  employeeIncentives,
}: EmployeeRowProps) => {
  const {
    salesIncentiveActual,
    salesIncentiveTarget,
    thumbsUpActual,
    thumbsUpTarget,
    user,
    startTime,
    endTime,
    position,
    responsibility,
    note,
    isFirstActivity,
    breaks,
  } = employee
  // The responsibility select options merge all available responsibilities configured in Admin + existing employee's responsibilities, e.g. some previously enabled responsibility saved for an employee but later this responsibility is disabled
  const responsibilityOptions = uniq([...shiftResponsibilities.map((s) => s.name), ...responsibility])
    .filter((s) => s !== 'Other')
    .sort()
  const salesIncentiveTargetReachedClass =
    salesIncentiveActual >= salesIncentiveTarget ? css.targetReached : css.targetNotReached
  const thumbsUpTargetReachedClass = thumbsUpActual >= thumbsUpTarget ? css.targetReached : css.targetNotReached
  // An additional condition used to only give "user" type permission to edit notes but not other fields.
  const isUserRole = userType === 'User'
  const currentTime = moment()
  const start = getMomentFromTime(startTime, false)
  const end = getMomentFromTime(endTime, false)
  const shiftOver = start.isBefore(end) && currentTime.diff(end, 'minutes') >= 30

  const onBreakCheckboxChecked = async (add: boolean) => {
    const keptBreaks = add ? breaks + 1 : breaks - 1
    await valueChanged(idx, 'breaks', keptBreaks)
    submit()
  }

  const renderBreakCheckboxes = () => {
    const keptBreaks = breaks | 0
    return [
      <Checkbox
        key={`breaks_1_${idx}`}
        checked={keptBreaks > 0}
        disabled={!isToday}
        size={'small'}
        onChange={(e) => onBreakCheckboxChecked(e.target.checked)}
        color={'primary'}
      />,
      <Checkbox
        key={`breaks_2_${idx}`}
        checked={keptBreaks > 1}
        disabled={keptBreaks < 1 || !isToday}
        size={'small'}
        onChange={(e) => onBreakCheckboxChecked(e.target.checked)}
        color={'primary'}
      />,
      <Checkbox
        key={`breaks_3_${idx}`}
        checked={keptBreaks > 2}
        disabled={keptBreaks < 2 || !isToday}
        size={'small'}
        onChange={(e) => onBreakCheckboxChecked(e.target.checked)}
        color={'primary'}
      />,
    ]
  }

  return (
    <React.Fragment>
      <tr
        className={cn(css.listRow, {
          [css.listRowHighlighted]: isFirstActivity,
          [css.shiftOver]: shiftOver && isToday,
        })}
      >
        <td className={css.tableStaffName}>
          <div>
            {user.name}
            {isFirstActivity && hasMultipleRows && !isEditing && <RemoveIcon onClick={showLess} />}
          </div>
        </td>

        {!isEditing && (
          <td className={cn(css.ignoreShiftOver, css.checkboxCell)}>
            <div className={css.checkboxes}>{isFirstActivity && renderBreakCheckboxes()}</div>
          </td>
        )}

        <td className={css.tableTime}>
          {!isUserRole && isEditing && isToday ? (
            <React.Fragment>
              <TextField
                id={`startTime-${employee.userId}-${idx}`}
                type='time'
                className={css.timeInput}
                defaultValue={startTime}
                onChange={(e) => valueChanged(idx, 'startTime', e.target.value)}
                inputProps={{
                  step: 300, // 5 min
                }}
              />

              <TextField
                id={`endTime-${employee.userId}-${idx}`}
                type='time'
                className={css.timeInput}
                defaultValue={endTime}
                onChange={(e) => valueChanged(idx, 'endTime', e.target.value)}
                inputProps={{
                  step: 300, // 5 min
                }}
              />
            </React.Fragment>
          ) : (
            <React.Fragment>
              {startTime} – {endTime}
            </React.Fragment>
          )}
        </td>
        <td className={css.position}>
          {!isUserRole && isEditing && isToday ? (
            <FormControl className={css.positionSelect}>
              <Select
                fullWidth
                onChange={(e) => valueChanged(idx, 'position', e.target.value)}
                value={position}
                inputProps={{
                  'aria-label': i18n.t('staffingPlan.position'),
                  id: `position-${employee.userId}-${idx}`,
                }}
              >
                {positionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {i18n.t(`staffingPlan.positions.${option.label}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <span className={css.positionTxt} onClick={() => openEmployeeRowDialog(activityDescription)}>
              {i18n.t(`staffingPlan.positions.${position}`)}
            </span>
          )}
        </td>
        <td className={css.responsibility}>
          {!isUserRole && isEditing ? (
            <FormControl className={css.responsibilitySelect}>
              <ResponsibilitySelect
                fullWidth
                multiple
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .MuiListItemText-primary': {
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                      },
                    },
                  },
                }}
                onChange={(e) => valueChanged(idx, 'responsibility', e.target.value)}
                value={responsibility.filter((r) => r !== 'Other')}
                renderValue={(selected) => {
                  const names = selected.filter((r) => r !== 'Other')
                  if (names.length === 0) return ''
                  return (
                    <span className={css.responsibilityRenderValue}>
                      {names.map((r) => i18n.t([`staffingPlan.responsibilities.${r}`, r])).join(', ')}
                    </span>
                  )
                }}
                inputProps={{
                  'aria-label': i18n.t('staffingPlan.responsibility'),
                  id: `responsibility-${employee.userId}-${idx}`,
                }}
              >
                {responsibilityOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    <Checkbox
                      checked={responsibility.filter((r) => r !== 'Other').includes(option)}
                      aria-hidden='true'
                      tabIndex={-1}
                      disableRipple
                      size='small'
                      className={css.responsibilityCheckbox}
                    />
                    <ListItemText primary={i18n.t([`staffingPlan.responsibilities.${option}`, option])} />
                  </MenuItem>
                ))}
              </ResponsibilitySelect>
            </FormControl>
          ) : (
            responsibility
              .filter((r) => r !== 'Other')
              .map((r) => i18n.t([`staffingPlan.responsibilities.${r}`, r]))
              .join(', ')
          )}
        </td>
        {salesIncentiveTarget !== undefined && userType !== 'User' && (
          <td className={css.incentives}>
            {!isUserRole && isEditing ? (
              <TextField
                className={css.alignRight}
                id={`salesIncentiveTarget-${employee.userId}-${idx}`}
                placeholder='0'
                type='number'
                value={salesIncentiveTarget || ''}
                onChange={(e) => valueChanged(idx, 'salesIncentiveTarget', e.target.value)}
              />
            ) : (
              <span className={salesIncentiveTargetReachedClass}>
                {isFirstActivity && `${employeeIncentives.sia} / ${employeeIncentives.sit || 0}`}
              </span>
            )}
          </td>
        )}
        {thumbsUpActual !== undefined && userType !== 'User' && (
          <td className={css.incentives}>
            {!isUserRole && isEditing ? (
              <TextField
                className={css.alignRight}
                id={`thumbsUpTarget-${employee.userId}-${idx}`}
                type='number'
                placeholder='0'
                value={thumbsUpTarget || ''}
                onChange={(e) => valueChanged(idx, 'thumbsUpTarget', e.target.value)}
              />
            ) : (
              <span className={thumbsUpTargetReachedClass}>
                {isFirstActivity && `${employeeIncentives.tua} / ${employeeIncentives.tut || 0}`}
              </span>
            )}
          </td>
        )}
        {isEditing && (
          <td className={cn(css.rowActions, css.ignoreShiftOver)}>
            {isFirstActivity && <NoteButton onClick={toggleEditNote} />}
            {!isUserRole && isToday && <DeleteButton onClick={() => removeStaffMember(employee.userId, idx)} />}
          </td>
        )}
      </tr>
      {(!isEditing || !editNote) && note && note !== '' ? (
        <tr>
          <td className={css.note}>{note}</td>
        </tr>
      ) : null}
      {editNote && isEditing && (
        <tr>
          <td colSpan={7}>
            <TextField
              id={`userNote-${employee.userId}-${idx}`}
              className={css.noteInput}
              value={note}
              onChange={(e) => valueChanged(idx, 'note', e.target.value)}
              label={i18n.t('staffingPlan.addNote')}
              margin='normal'
              variant='outlined'
              multiline
              autoFocus={true}
              maxRows='3'
            />
            <span className={css.noteActions}>
              <DoneButton onClick={toggleEditNote} />
              <DeleteButton
                onClick={() => {
                  valueChanged(idx, 'note', '')
                  toggleEditNote()
                }}
              />
            </span>
          </td>
        </tr>
      )}
    </React.Fragment>
  )
}

export default EmployeeRow
